// src/pages/TermsOfService.js
import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

const TermsOfService = () => {
  return (
    <div>
      <Helmet>
        <title>Terms of Service - YouthXConnect</title>
        <meta
          name="description"
          content="Read the Terms of Service for YouthXConnect to understand the rules and regulations governing the use of our website and services."
        />
        <meta
          name="keywords"
          content="Terms of Service, YouthXConnect, Terms, Conditions, User Agreement"
        />
        <meta property="og:title" content="Terms of Service - YouthXConnect" />
        <meta
          property="og:description"
          content="Read the Terms of Service for YouthXConnect to understand the rules and regulations governing the use of our website and services."
        />
        <meta property="og:image" content="%PUBLIC_URL%/terms-of-service-og.png" />
        <meta property="og:url" content="https://www.youthxconnect.com/terms-of-service" />
        <meta name="twitter:title" content="Terms of Service - YouthXConnect" />
        <meta
          name="twitter:description"
          content="Read the Terms of Service for YouthXConnect to understand the rules and regulations governing the use of our website and services."
        />
        <meta name="twitter:image" content="%PUBLIC_URL%/terms-of-service-twitter.png" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      <motion.div
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-20 max-w-4xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.h1
          className="text-3xl sm:text-4xl font-bold mb-6 text-center text-gray-900"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          Terms of Service
        </motion.h1>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          Last updated: October 31, 2024
        </motion.p>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          Please read these Terms of Service ("Agreement") carefully. By accessing or using the Services, you agree to be bound by all terms and conditions contained herein. If you do not agree to all of these terms, do not use the Services. Your use of the Services constitutes your acceptance of and agreement to abide by each of these terms and conditions.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          Disclaimer of Warranties
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          The Services are provided "as is" and "as available" without warranties of any kind, whether express, implied, statutory, or otherwise. To the maximum extent permissible under applicable law, YouthXConnect hereby disclaims all warranties, whether express, implied, statutory, or otherwise, including but not limited to any implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
        >
          Comprehensive Limitation of Liability
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
          In no event shall YouthXConnect, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
          <ul className="list-disc pl-6 mt-2">
            <li>Your access to or use of or inability to access or use the Services</li>
            <li>Any conduct or content of any third party on the Services</li>
            <li>Any content obtained from the Services</li>
            <li>Unauthorized access, use or alteration of your transmissions or content</li>
            <li>Any bugs, viruses, trojan horses, or the like that may be transmitted to or through our Services</li>
            <li>Any errors or omissions in any content or for any loss or damage incurred as a result of the use of any content</li>
            <li>Any financial losses, including but not limited to loss of money, profits, or business opportunities</li>
          </ul>
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9 }}
        >
          Indemnification
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }}>
          You agree to defend, indemnify, and hold harmless YouthXConnect, its parent company, officers, directors, employees, agents, licensors, and suppliers from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Services, including, but not limited to, any use of the Services' content other than as expressly authorized.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.1 }}
        >
          Force Majeure and External Events
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
          Under no circumstances shall YouthXConnect be held liable or responsible for any delay or failure to perform resulting directly or indirectly from acts of nature, forces, or causes beyond its reasonable control, including, without limitation, Internet failures, computer equipment failures, telecommunication equipment failures, other equipment failures, electrical power failures, strikes, labor disputes, riots, insurrections, civil disturbances, shortages of labor or materials, fires, floods, storms, explosions, acts of God, war, governmental actions, orders of domestic or foreign courts or tribunals, non-performance of third parties, or loss of or fluctuations in heat, light, or air conditioning.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.3 }}
        >
          Severability and Waiver
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}>
          If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect. No waiver by YouthXConnect of any term or condition set out in these Terms shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition, and any failure by YouthXConnect to assert a right or provision under these Terms shall not constitute a waiver of such right or provision.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5 }}
        >
          Use of the Site
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}>
          You agree to use the site only for lawful purposes and in a way that does not infringe the rights of others or restrict their use and enjoyment of the site.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.7 }}
        >
          Intellectual Property
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}>
          All content, trademarks, and data on this site are the property of <strong>YouthXConnect</strong> and are protected by Indian and international intellectual property laws.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.9 }}
        >
          User Responsibilities
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0 }}>
          Users are responsible for maintaining the confidentiality of their account information and for all activities that occur under their account.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2.1 }}
        >
          Governing Law
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }}>
          These terms are governed by and construed in accordance with the laws of India. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in India.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2.3 }}
        >
          Changes to Terms
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.4 }}>
          <strong>YouthXConnect</strong> reserves the right to modify these terms at any time. Changes will be effective immediately upon posting on the site.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2.5 }}
        >
          Contact Information
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.6 }}>
          For any questions regarding these Terms of Service, please contact us at <a href="mailto:connect@YouthXConnect.com" className="text-blue-500 underline">connect@YouthXConnect.com</a>.
        </motion.p>
      </motion.div>
      
    </div>
  );
};

export default TermsOfService;
